import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { 
  ArrowDownLeft,
  Clock,
  CheckCircle2,
  Download,
  Loader2,
  FileText,
  RefreshCw
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function BrokerWallet() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  // SSoT-based commission state
  const [ssotWallet, setSsotWallet] = useState(null);
  const [ssotAllocations, setSsotAllocations] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    gstAmount: 0,
    bankName: '',
    accountNumber: '',
    ifscCode: ''
  });
  const [submittingInvoice, setSubmittingInvoice] = useState(false);

  // Get user with validation
  const getUserId = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;
      const user = JSON.parse(userData);
      return user?.id || null;
    } catch {
      return null;
    }
  };

  const userId = getUserId();

  useEffect(() => {
    // Only fetch if we have a valid user ID
    if (userId) {
      fetchData();
    } else {
      setError('Session expired. Please log in again.');
      setLoading(false);
    }
  }, [userId]);

  const fetchData = async () => {
    // Double-check userId before API calls
    const currentUserId = getUserId();
    if (!currentUserId) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Fetch wallet totals and allocations from SSoT endpoints only
      const [walletRes, allocationsRes] = await Promise.all([
        api.get(`/broker/${currentUserId}/wallet-ssot`),
        api.get(`/broker/${currentUserId}/commissions-ssot`)
      ]);
      
      setSsotWallet(walletRes.data);
      setSsotAllocations(allocationsRes.data.allocations || []);
      
      // Fetch payment history from payments_ledger
      try {
        const historyRes = await api.get(`/broker/${currentUserId}/payment-history`);
        setPaymentHistory(historyRes.data.payments || []);
      } catch (historyError) {
        // Payment history is optional - don't fail the whole page
        console.warn('Payment history not available:', historyError);
        setPaymentHistory([]);
      }
      
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load wallet data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Submit invoice for allocation
  const submitInvoice = async () => {
    if (!selectedAllocation || !invoiceForm.invoiceNumber || !invoiceForm.bankName) {
      toast.error('Please fill all required fields');
      return;
    }

    const currentUserId = getUserId();
    if (!currentUserId) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    setSubmittingInvoice(true);
    try {
      await api.post(`/broker/${currentUserId}/commissions/${selectedAllocation.unitId}/generate-invoice`, {
        invoiceNumber: invoiceForm.invoiceNumber,
        invoiceDate: invoiceForm.invoiceDate,
        gstAmount: invoiceForm.gstAmount || 0,
        bankDetails: {
          bankName: invoiceForm.bankName,
          accountNumber: invoiceForm.accountNumber,
          ifscCode: invoiceForm.ifscCode
        }
      });
      toast.success('Invoice submitted successfully!');
      setShowInvoiceModal(false);
      setSelectedAllocation(null);
      setInvoiceForm({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        gstAmount: 0,
        bankName: '',
        accountNumber: '',
        ifscCode: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit invoice');
    } finally {
      setSubmittingInvoice(false);
    }
  };

  // Calculate totals from SSoT
  const totalEarnings = ssotWallet 
    ? (ssotWallet.pending || 0) + (ssotWallet.eligible || 0) + (ssotWallet.invoiced || 0) + (ssotWallet.approved || 0) + (ssotWallet.paid || 0)
    : 0;

  return (
    <div className="min-h-screen bg-[#030303] flex">
      <BrokerSidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-1">
              Earnings & Payouts
            </h1>
            <p className="text-white/50 text-sm">Track your commissions and payment status</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-6 text-center max-w-md">
              <p className="text-red-400 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/auth'}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                Go to Login
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Commission Status - SSoT Based */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-400" />
                    Commission Status
                  </h3>
                  <p className="text-xs text-white/40 mt-1">Real-time unit-level tracking</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/40">Total Earnings</p>
                  <p className="text-xl font-mono text-white">{formatINR(totalEarnings)}</p>
                </div>
              </div>

              {/* Status Summary Tiles */}
              <div className="grid grid-cols-5 gap-4 p-4 border-b border-white/5">
                <div className="text-center p-3 bg-gray-500/5 border border-gray-500/20 rounded-sm">
                  <p className="text-xs text-gray-400 uppercase mb-1">Booked</p>
                  <p className="text-lg font-mono text-gray-400">{formatINR(ssotWallet?.pending || 0)}</p>
                  <p className="text-xs text-white/30">Awaiting dev payment</p>
                </div>
                <div className="text-center p-3 bg-blue-500/5 border border-blue-500/20 rounded-sm">
                  <p className="text-xs text-blue-400 uppercase mb-1">Eligible</p>
                  <p className="text-lg font-mono text-blue-400">{formatINR(ssotWallet?.eligible || 0)}</p>
                  <p className="text-xs text-white/30">Ready to invoice</p>
                </div>
                <div className="text-center p-3 bg-purple-500/5 border border-purple-500/20 rounded-sm">
                  <p className="text-xs text-purple-400 uppercase mb-1">Invoiced</p>
                  <p className="text-lg font-mono text-purple-400">{formatINR(ssotWallet?.invoiced || 0)}</p>
                  <p className="text-xs text-white/30">Under review</p>
                </div>
                <div className="text-center p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-sm">
                  <p className="text-xs text-cyan-400 uppercase mb-1">Approved</p>
                  <p className="text-lg font-mono text-cyan-400">{formatINR(ssotWallet?.approved || 0)}</p>
                  <p className="text-xs text-white/30">Payment processing</p>
                </div>
                <div className="text-center p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
                  <p className="text-xs text-emerald-400 uppercase mb-1">Paid</p>
                  <p className="text-lg font-mono text-emerald-400">{formatINR(ssotWallet?.paid || 0)}</p>
                  <p className="text-xs text-white/30">Received</p>
                </div>
              </div>

              {/* Allocations List */}
              {ssotAllocations.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40">No commissions yet</p>
                  <p className="text-xs text-white/30 mt-1">Commissions appear when your referred investors make investments</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {ssotAllocations.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div>
                        <p className="text-sm text-white font-medium">{item.unitName}</p>
                        <p className="text-xs text-white/40">{item.projectName}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-white/30">
                            Tier: {item.allocation?.tierAtInvestment} ({item.allocation?.tierRateUV}%)
                          </span>
                          <span className="text-xs text-white/30">•</span>
                          <span className="text-xs text-white/30">
                            Capital: {formatINR(item.allocation?.capitalRaised)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-mono text-white">{formatINR(item.allocation?.commissionFinal)}</p>
                          <p className="text-xs text-white/40">
                            {item.allocation?.effectiveRateOnCapital}% on capital
                          </p>
                        </div>
                        {item.allocation?.outflowStatus === 'ELIGIBLE' ? (
                          <Button
                            size="sm"
                            className="bg-blue-500 hover:bg-blue-600 text-white"
                            onClick={() => {
                              setSelectedAllocation(item);
                              setShowInvoiceModal(true);
                            }}
                            data-testid={`generate-invoice-${item.unitId}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Generate Invoice
                          </Button>
                        ) : (
                          <span className={`px-3 py-1.5 text-xs font-medium rounded-sm ${
                            item.allocation?.outflowStatus === 'PAID' 
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                              : item.allocation?.outflowStatus === 'INVOICED'
                              ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                              : item.allocation?.outflowStatus === 'APPROVED'
                              ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                              : 'bg-gray-500/10 text-gray-400 border border-gray-500/30'
                          }`}>
                            {item.allocation?.outflowStatus === 'BOOKED' ? 'Awaiting Dev Payment' : item.allocation?.outflowStatus}
                          </span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment History - From payments_ledger */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm" data-testid="payment-history-section">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="text-sm font-medium text-white">Payment History</h3>
                <Button variant="ghost" size="sm" className="text-white/50">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {paymentHistory.length === 0 ? (
                <div className="p-12 text-center">
                  <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40">No payment history yet</p>
                  <p className="text-xs text-white/30 mt-1">Payments will appear here once processed</p>
                </div>
              ) : (
                paymentHistory.map((payment, index) => (
                  <motion.div
                    key={payment.transactionId || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`payment-row-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-[#4ADE80]/10">
                        <ArrowDownLeft className="w-5 h-5 text-[#4ADE80]" />
                      </div>
                      <div>
                        <p className="text-white text-sm">{payment.description || 'Commission Payout'}</p>
                        {payment.referenceId && (
                          <p className="text-xs text-white/40">Ref: {payment.referenceId}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg text-[#4ADE80]">
                        +{formatINR(payment.amount)}
                      </p>
                      <div className="flex items-center gap-2 justify-end">
                        <span className="text-xs text-white/40">
                          {payment.completedAt ? new Date(payment.completedAt).toLocaleDateString() : 
                           payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : '-'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${
                          payment.status === 'COMPLETED' 
                            ? 'bg-emerald-500/10 text-emerald-400' 
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {payment.status || 'COMPLETED'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Generate Invoice Modal */}
      {showInvoiceModal && selectedAllocation && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-md"
          >
            <div className="p-6 border-b border-white/5">
              <h2 className="text-xl font-light text-white">Generate Invoice</h2>
              <p className="text-sm text-white/50 mt-1">
                {selectedAllocation.unitName} - {formatINR(selectedAllocation.allocation?.commissionFinal)}
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Invoice Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/40 uppercase block mb-2">Invoice Number *</label>
                  <Input
                    type="text"
                    placeholder="INV-001"
                    value={invoiceForm.invoiceNumber}
                    onChange={(e) => setInvoiceForm({...invoiceForm, invoiceNumber: e.target.value})}
                    className="bg-[#030303] border-white/10 text-white"
                    data-testid="invoice-number-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/40 uppercase block mb-2">Invoice Date</label>
                  <Input
                    type="date"
                    value={invoiceForm.invoiceDate}
                    onChange={(e) => setInvoiceForm({...invoiceForm, invoiceDate: e.target.value})}
                    className="bg-[#030303] border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-white/40 uppercase block mb-2">GST Amount (if applicable)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={invoiceForm.gstAmount}
                  onChange={(e) => setInvoiceForm({...invoiceForm, gstAmount: parseFloat(e.target.value) || 0})}
                  className="bg-[#030303] border-white/10 text-white"
                />
              </div>

              {/* Bank Details */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-white/40 uppercase mb-4">Bank Details for Payout</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-white/40 block mb-2">Bank Name *</label>
                    <Input
                      type="text"
                      placeholder="HDFC Bank"
                      value={invoiceForm.bankName}
                      onChange={(e) => setInvoiceForm({...invoiceForm, bankName: e.target.value})}
                      className="bg-[#030303] border-white/10 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-white/40 block mb-2">Account Number *</label>
                      <Input
                        type="text"
                        placeholder="1234567890"
                        value={invoiceForm.accountNumber}
                        onChange={(e) => setInvoiceForm({...invoiceForm, accountNumber: e.target.value})}
                        className="bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 block mb-2">IFSC Code *</label>
                      <Input
                        type="text"
                        placeholder="HDFC0001234"
                        value={invoiceForm.ifscCode}
                        onChange={(e) => setInvoiceForm({...invoiceForm, ifscCode: e.target.value})}
                        className="bg-[#030303] border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/5 flex gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setSelectedAllocation(null);
                }}
                className="flex-1 border border-white/10"
                disabled={submittingInvoice}
              >
                Cancel
              </Button>
              <Button
                onClick={submitInvoice}
                disabled={submittingInvoice || !invoiceForm.invoiceNumber || !invoiceForm.bankName}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="submit-invoice-btn"
              >
                {submittingInvoice ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  'Submit Invoice'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
